import React from "react";
import MainLayout from "../../layouts/main_layout/main_layout";
import { Avatar, Box, CircularProgress, Grid, Typography } from "@mui/material";
import {
  MAIN_THEME_COLOR,
  SECONDARY_THEME_COLOR,
} from "../../providers/theme/colors/colors";
import { KeyboardDoubleArrowRight } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { firestore } from "../../index";
import useAboutLeaders from "./use_about_leaders";
import { buildAboutSummary, AboutLeader } from "./about_data";
import {
  DeaneryMember,
  defaultDeaneryMembers,
  normalizeDeaneryMember,
  sortDeaneryMembers,
} from "../deanery_youth_commission/deanery_data";

function ProfileCard({ person }: { person: AboutLeader | DeaneryMember }) {
  const subtitle = "deanery" in person ? person.deanery : person.place;
  const designation = "designation" in person ? person.designation : "";

  return (
    <Box
      p={2}
      display={"flex"}
      flexDirection={"column"}
      alignItems={"center"}
      justifyContent={"flex-start"}
      width={"100%"}
      height={"100%"}
      sx={{
        border: `1.5px solid ${MAIN_THEME_COLOR}`,
        borderRadius: 3,
        background:
          "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(244,250,255,0.9) 100%)",
        boxShadow: "0 10px 24px rgba(64, 162, 227, 0.08)",
      }}
    >
      {"img" in person && person.img ? (
        <Box
          component="img"
          src={person.img}
          alt={person.name}
          sx={{
            width: { xs: 150, sm: 170, md: 180 },
            height: { xs: 180, sm: 200, md: 215 },
            borderRadius: 3,
            objectFit: "cover",
            objectPosition: "center top",
            border: `3px solid ${SECONDARY_THEME_COLOR}`,
          }}
        />
      ) : (
        <Avatar
          sx={{
            width: { xs: 150, sm: 170, md: 180 },
            height: { xs: 180, sm: 200, md: 215 },
            borderRadius: 3,
            bgcolor: `${SECONDARY_THEME_COLOR}33`,
            color: MAIN_THEME_COLOR,
            border: `3px solid ${SECONDARY_THEME_COLOR}`,
            fontSize: "3rem",
            fontWeight: 700,
          }}
        >
          {person.name.charAt(0)}
        </Avatar>
      )}
      <Typography
        mt={1.5}
        variant="subtitle1"
        fontWeight={700}
        textAlign={"center"}
      >
        {person.name}
      </Typography>
      {subtitle ? (
        <Typography
          variant="body2"
          color="text.secondary"
          mt={0.5}
          textAlign={"center"}
        >
          {subtitle}
        </Typography>
      ) : null}
      <Typography
        variant="body2"
        fontWeight={700}
        color="text.primary"
        mt={0.75}
        textAlign={"center"}
        sx={{ fontSize: "0.9rem", lineHeight: 1.5 }}
      >
        {designation}
      </Typography>
    </Box>
  );
}

export default function AboutUs() {
  const { leaders, loading: leadersLoading } = useAboutLeaders();
  const [deaneryMembers, setDeaneryMembers] = useState<DeaneryMember[]>(sortDeaneryMembers(defaultDeaneryMembers));
  const [deaneryLoading, setDeaneryLoading] = useState(true);

  useEffect(() => {
    async function fetchDeaneryMembers() {
      try {
        const snapshot = await getDocs(collection(firestore, "deanery_members"));
        const members = sortDeaneryMembers(
          snapshot.docs.map((memberDoc) =>
            normalizeDeaneryMember({
              docId: memberDoc.id,
              ...(memberDoc.data() as Omit<DeaneryMember, "docId">),
            }),
          ),
        );
        setDeaneryMembers(members.length ? members : sortDeaneryMembers(defaultDeaneryMembers));
      } catch (error) {
        console.error("Failed to load deanery members", error);
        setDeaneryMembers(sortDeaneryMembers(defaultDeaneryMembers));
      } finally {
        setDeaneryLoading(false);
      }
    }

    fetchDeaneryMembers();
  }, []);

  const deaneryDirectors = [...deaneryMembers]
    .filter((member) => member.designation.toLowerCase().includes("director"))
    .sort((memberA, memberB) => {
      const getDirectorPriority = (organization: string) => {
        switch (organization) {
          case "ICYM-YCS":
            return 1;
          case "ICYM":
            return 2;
          case "YCS":
            return 3;
          default:
            return 4;
        }
      };

      const priorityDifference =
        getDirectorPriority(memberA.organization) - getDirectorPriority(memberB.organization);

      if (priorityDifference !== 0) {
        return priorityDifference;
      }

      return 0;
    });

  return (
    <MainLayout>
      <Box mt={2} mb={4}>
        <Typography variant="h5" fontWeight={"600"} textAlign={"center"}>
          SAMMITRA, Youth Commission - Diocese of Udupi
        </Typography>
        <Typography
          variant="body1"
          mt={3}
          sx={{
            maxWidth: 980,
            mx: "auto",
            textAlign: "center",
            lineHeight: 1.8,
          }}
        >
          {buildAboutSummary(leaders)}
        </Typography>

        <Box mt={5}>
          <Typography
            variant="h6"
            fontWeight={700}
            textAlign={"center"}
            color={MAIN_THEME_COLOR}
          >
            Leadership Team
          </Typography>
          <Typography
            variant="body2"
            textAlign={"center"}
            color="text.secondary"
            mt={1}
          >
            Diocese leadership and youth commission office bearers
          </Typography>
          {leadersLoading ? (
            <Box display="flex" justifyContent="center" mt={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid justifyContent={"center"} container spacing={3} mt={2}>
              {leaders.map((member) => (
                <Grid item md={4} sm={6} xs={12} key={member.docId || `${member.rank}-${member.name}`}>
                  <ProfileCard person={member} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        <Box mt={6}>
          <Typography
            variant="h6"
            fontWeight={700}
            textAlign={"center"}
            color={MAIN_THEME_COLOR}
          >
            Deanery Directors
          </Typography>
          <Typography
            variant="body2"
            textAlign={"center"}
            color="text.secondary"
            mt={1}
          >
            The 5 deaneries and their directors
          </Typography>
          {deaneryLoading ? (
            <Box display="flex" justifyContent="center" mt={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid justifyContent={"center"} container spacing={2.5} mt={2}>
              {deaneryDirectors.map((member, index) => (
                <Grid
                  item
                  md={index < 3 ? 4 : 3}
                  lg={index < 3 ? 4 : 3}
                  sm={6}
                  xs={12}
                  key={member.docId || `${member.deanery}-${member.name}`}
                >
                  <ProfileCard person={member} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        <Box display={"flex"} flexDirection={"column"} mt={4}>
          <Typography variant="body1" fontWeight={600}>
            The Diocese has :
          </Typography>

          <Box display={"flex"} alignItems={"center"}>
            <KeyboardDoubleArrowRight />
            <Box
              display={"flex"}
              alignItems={"center"}
              sx={{
                border: `2px solid ${MAIN_THEME_COLOR}`,
                borderRadius: 1,
              }}
              height={"fit-content"}
              py={0.3}
              mx={1}
              mt={1}
            >
              <Typography variant="button" mx={1}>
                50 ICYM units
              </Typography>
            </Box>
          </Box>
          <Box display={"flex"} alignItems={"center"}>
            <KeyboardDoubleArrowRight />
            <Box
              sx={{
                border: `2px solid ${MAIN_THEME_COLOR}`,
                borderRadius: 1,
              }}
              height={"fit-content"}
              py={0.3}
              mx={1}
              my={1}
            >
              <Typography variant="button" mx={1}>
                30 active YSM schools
              </Typography>
            </Box>
          </Box>

          <Box display={"flex"} alignItems={"center"}>
            <KeyboardDoubleArrowRight />
            <Box
              sx={{
                border: `2px solid ${MAIN_THEME_COLOR}`,
                borderRadius: 1,
              }}
              height={"fit-content"}
              py={0.3}
              mx={1}
            >
              <Typography variant="button" mx={1}>
                48 YCS units
              </Typography>
            </Box>
          </Box>
        </Box>
        <Typography variant="body1" fontWeight={600} mt={2}>
          Youth Commission Vision: Educated and united catholic youth for
          Christian leadership
        </Typography>
        <Typography variant="body1" fontWeight={600} mt={2}>
          Mission: To form catholic youth as value based leaders in the society.
          To motivate the youth to work for the development of the society.
        </Typography>
      </Box>
    </MainLayout>
  );
}
